import React, { useState } from "react";
import * as Styled from "./styles";
import { ASSETS } from "../../../../utils/config";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedMenu } from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  handleCloseMenu,
  handleMenuChange,
} from "../../../../redux/reducers/segmentBuilder";
import useSegmentBuilder from "../../../../hooks/useSegmentBuilder";
import { useEditor } from "@craftjs/core";
import { StyledTooltip } from "../../../IyugaTooltip";
import { CAPTURE_ENABLED } from "../../../../constants/featureFlags";

// Exported for the flag-gated rail-item guard test (SideMenu.capture.test.jsx).
export const menuItems = [
  {
    id: 1,
    icon: ASSETS.segmentEditor.Setting,
    text: "Wheel",
    menuInnerProps: {},
    iconWrapperProps: {},
    textWrapperProps: { padding: "0px 0px 0px 5px" },
  },
  {
    id: 2,
    icon: ASSETS.segmentEditor.Segment,
    text: "Segment",
    menuInnerProps: { width: "51px", height: "60px" },
    iconWrapperProps: {
      width: "40px",
      height: "40px",
      margin: "0px 0px 0px 6px",
    },
    textWrapperProps: {},
  },
  {
    id: 3,
    icon: ASSETS.segmentEditor.Edit,
    text: "Build",
    menuInnerProps: { width: "40px", height: "60px" },
    iconWrapperProps: { width: "40px", height: "40px" },
    textWrapperProps: { padding: "0px 0px 0px 5px" },
  },
  {
    id: 4,
    icon: ASSETS.segmentEditor.Hotbar,
    text: "Hotbar",
    menuInnerProps: { width: "40px", height: "60px" },
    iconWrapperProps: { width: "40px", height: "40px" },
    textWrapperProps: { padding: "0px 0px 0px 5px" },
  },
  {
    id: 5,
    icon: ASSETS.segmentEditor.Business,
    text: "Business",
    menuInnerProps: { width: "40px", height: "60px" },
    iconWrapperProps: { width: "40px", height: "40px" },
    textWrapperProps: {},
  },
  {
    id: 6,
    icon: ASSETS.segmentEditor.SegmentLibrary,
    text: "Segment Library",
    menuInnerProps: { width: "40px", height: "60px" },
    iconWrapperProps: { width: "40px", height: "40px" },
    textWrapperProps: { padding: "0px 0px 0px 5px" },
  },
  // GR-3 — Capture rail entry (opens the owner-only "Captured items on this
  // wheel" drawer, wired via SegmentBuilder/index.jsx's componentMap at
  // selectedMenu === 8). Flag-gated: OFF => this rail item is not rendered
  // and the rail is byte-identical to today. Ported from sandbox as-is minus
  // the unrelated WHEEL_COPILOT_ENABLED rail item (out of Capture's scope).
  ...(CAPTURE_ENABLED
    ? [
        {
          id: 8,
          icon: ASSETS.segmentEditor.Capture,
          text: "Capture",
          tooltip: "Capture",
          menuInnerProps: { width: "40px", height: "60px" },
          iconWrapperProps: { width: "40px", height: "40px" },
          textWrapperProps: { padding: "0px 0px 0px 5px" },
        },
      ]
    : []),
];

const MenuItem = ({
  id,
  icon,
  text,
  tooltip,
  selected,
  handleMenuChange,
  menuInnerProps,
  iconWrapperProps,
  textWrapperProps,
}) => {
  const container = (
    <Styled.MenuContainer
      onClick={() => handleMenuChange(id)}
      backgroundColor={selected === id ? "white" : "#f9f5ff"}
    >
      <Styled.MenuIconWrapper {...iconWrapperProps}>
        <img src={icon} width={38} height={38} alt={`${text} Icon`} />
      </Styled.MenuIconWrapper>
      <Styled.MenuTextWrapper>
        <Styled.MenuText {...textWrapperProps}>{text}</Styled.MenuText>
      </Styled.MenuTextWrapper>
    </Styled.MenuContainer>
  );

  // ISP-9999 (fix-tooltip-stays-open-after-click-capture-surfaces, MEASURED
  // web-stg /builder/5399 at 820, 2026-09-09): a plain hover-driven Tooltip
  // never learns that a click just opened this item's pane, so the "Capture"
  // bubble stayed pinned beside the rail after the pane took over. The open
  // prop is now driven off the same `selected` state that opens the pane —
  // forced shut the instant this item becomes the active one, hover or not.
  const [hovered, setHovered] = useState(false);
  const isPaneOpen = selected === id;

  return tooltip ? (
    <StyledTooltip
      title={tooltip}
      placement="right"
      arrow
      open={hovered && !isPaneOpen}
      onOpen={() => setHovered(true)}
      onClose={() => setHovered(false)}
    >
      {container}
    </StyledTooltip>
  ) : (
    container
  );
};

const SideMenu = () => {
  const selectedMenu = useSelector(getSelectedMenu);
  const { isToolSelected } = useSegmentBuilder();
  const { actions } = useEditor();
  const { selected } = isToolSelected() || {};
  const dispatch = useDispatch();

  const handleChange = (id) => {
    if (selectedMenu == 3 && selected && id == 3) {
      actions.selectNode(null);
      return;
    }
    if (selectedMenu == id) dispatch(handleCloseMenu());
    else dispatch(handleMenuChange({ num: id }));
  };

  return (
    <Styled.SideMenuMainDiv>
      {menuItems.map((item) => (
        <MenuItem
          key={item.id}
          id={item.id}
          icon={item.icon}
          text={item.text}
          tooltip={item.tooltip}
          selected={selectedMenu}
          handleMenuChange={handleChange}
          menuInnerProps={item.menuInnerProps}
          iconWrapperProps={item.iconWrapperProps}
          textWrapperProps={item.textWrapperProps}
        />
      ))}
    </Styled.SideMenuMainDiv>
  );
};

export default SideMenu;
